import { useTheme } from "@/lib/theme-context";
/**
 * NotifyBell — header bell with a single popover split into two sub-sections:
 *   1) Top   : browser push (FCM) enable/disable status & toggle.
 *   2) Bottom: in-app inbox (replies + reactions on your comments).
 *
 * RTL/LTR-aware, responsive (mobile = full-width sheet, desktop = panel).
 * Badge: shows unread inbox count, capped at "99+" (max two characters).
 * Opening the popover marks all visible items as read.
 */
import { useCallback, useEffect, useRef, useState } from "react";
// Note: cross-route hash navigation uses plain <a> (see NotifRow).
import {
  Bell, BellOff, BellRing, Loader2, Check, X as XIcon,
  MessageCircle, Heart, ThumbsUp, ThumbsDown, Frown, Angry, Inbox,
  ChevronDown, ChevronUp, Trash2,
} from "lucide-react";
import { ensureFcmToken } from "@/lib/fcm-token";
import { getDb } from "@/lib/firebase";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { useUserAuth } from "@/lib/user-auth";
import {
  useNotifications,
  markAllNotificationsRead,
  clearAllNotifications,
  type AppNotification,
  type ReactionKind,
} from "@/lib/firebase-data";

type Status = "idle" | "default" | "granted" | "denied" | "unsupported" | "working";

const REGISTERED_KEY = "rawan-fcm-registered-v1";
const PENDING_COMMENT_KEY = "rawan:pending-comment-deeplink-v1";

function readPermission(): Status {
  if (typeof window === "undefined") return "idle";
  if (!("Notification" in window)) return "unsupported";
  const p = Notification.permission;
  if (p === "granted") return "granted";
  if (p === "denied") return "denied";
  return "default";
}

function formatBadge(n: number): string {
  if (n <= 0) return "";
  if (n > 99) return "99+";
  return String(n);
}

function reactionIcon(kind: ReactionKind | null | undefined) {
  switch (kind) {
    case "loves":  return <Heart className="h-3.5 w-3.5 text-red-500 fill-red-500" />;
    case "likes":  return <ThumbsUp className="h-3.5 w-3.5 text-primary" />;
    case "sads":   return <Frown className="h-3.5 w-3.5 text-blue-400" />;
    case "angrys": return <Angry className="h-3.5 w-3.5 text-orange-500" />;
    case "bads":   return <ThumbsDown className="h-3.5 w-3.5 text-zinc-500" />;
    default:       return <MessageCircle className="h-3.5 w-3.5 text-primary" />;
  }
}

function timeAgo(ms: number, isAr: boolean): string {
  const diff = Math.max(1, Math.floor((Date.now() - ms) / 1000));
  const units: Array<[number, string, string]> = [
    [60, "ث", "s"], [3600, "د", "m"], [86400, "س", "h"],
    [86400 * 30, "ي", "d"], [86400 * 365, "ش", "mo"],
  ];
  for (let i = 0; i < units.length; i++) {
    const [step, ar, en] = units[i];
    if (diff < step) {
      const prev = i === 0 ? 1 : units[i - 1][0];
      const v = Math.max(1, Math.floor(diff / prev));
      return isAr ? `قبل ${v} ${ar}` : `${v}${en}`;
    }
  }
  return isAr ? `قبل ${Math.floor(diff / (86400 * 365))} سنة` : `${Math.floor(diff / (86400 * 365))}y`;
}

function storeCommentDeepLinkIntent(commentId: string, href: string) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      PENDING_COMMENT_KEY,
      JSON.stringify({ commentId, href: new URL(href, window.location.origin).href, ts: Date.now() }),
    );
  } catch {
    /* ignore private-mode storage errors */
  }
}

export function NotifyBell({ className = "" }: { className?: string }) {
  const { language } = useTheme();
  const { user } = useUserAuth();
  const [status, setStatus] = useState<Status>("idle");
  const [open, setOpen] = useState(false);
  const [working, setWorking] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const { items, unread } = useNotifications(user?.id ?? null);

  /* Keep the bell in sync with the real browser permission.
   * Reads once on mount and again whenever the tab regains focus / is
   * restored from bfcache — this prevents the "granted then reverts to a
   * plain bell after refresh" symptom by always reflecting the true state. */
  useEffect(() => {
    const sync = () => setStatus(readPermission());
    sync();
    document.addEventListener("visibilitychange", sync);
    window.addEventListener("focus", sync);
    window.addEventListener("pageshow", sync);
    return () => {
      document.removeEventListener("visibilitychange", sync);
      window.removeEventListener("focus", sync);
      window.removeEventListener("pageshow", sync);
    };
  }, []);

  /* outside click + escape */
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const registerToken = useCallback(async () => {
    const token = await ensureFcmToken();
    if (!token) return false;
    const db = getDb();
    if (!db) return false;
    try {
      await setDoc(doc(db, "fcmTokens", token), {
        token,
        uid: user?.id ?? null,
        lang: typeof navigator !== "undefined" ? navigator.language : null,
        ua: typeof navigator !== "undefined" ? navigator.userAgent : null,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      try { localStorage.setItem(REGISTERED_KEY, token); } catch { /* */ }
      return true;
    } catch { return false; }
  }, [user?.id]);

  /* Re-assert the FCM subscription whenever permission is granted.
   * Runs on every load (not just the first time) so the messaging service
   * worker + token are re-registered after a refresh, keeping notifications
   * actually active instead of silently dropping. */
  useEffect(() => {
    if (status !== "granted") return;
    let cancel = false;
    (async () => {
      try {
        const ok = await registerToken();
        if (!cancel && ok) setStatus("granted");
      } catch { /* */ }
    })();
    return () => { cancel = true; };
  }, [status, registerToken]);

  /* mark inbox as read when popover opens */
  useEffect(() => {
    if (!open || !user?.id) return;
    const unreadIds = items.filter((x) => !x.read).map((x) => x.id);
    if (unreadIds.length === 0) return;
    void markAllNotificationsRead(user.id, unreadIds);
  }, [open, items, user?.id]);

  /* reset clear-confirmation when popover closes */
  useEffect(() => { if (!open) setConfirmClear(false); }, [open]);

  async function handleClearAll() {
    if (!user?.id || clearing || items.length === 0) return;
    if (!confirmClear) { setConfirmClear(true); return; }
    setClearing(true);
    try { await clearAllNotifications(user.id); }
    finally { setClearing(false); setConfirmClear(false); }
  }

  async function toggleFcm() {
    if (working || status === "unsupported") return;
    if (status === "granted") {
      // already on — re-stamp token defensively
      setWorking(true);
      try { await registerToken(); } finally { setWorking(false); }
      return;
    }
    if (status === "denied") return;
    setWorking(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        setStatus(perm === "denied" ? "denied" : "default");
        return;
      }
      await registerToken();
      setStatus("granted");
    } catch {
      setStatus(readPermission());
    } finally {
      setWorking(false);
    }
  }

  const isAr = typeof document !== "undefined" && document.documentElement.dir === "rtl";
  const t = (ar: string, en: string, fr?: string) => (language === "ar" ? ar : language === "fr" ? (fr ?? en) : en);

  const Icon =
    working           ? Loader2
    : status === "granted" ? BellRing
    : status === "denied"  ? BellOff
    : Bell;

  const badge = formatBadge(unread);

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t("\u0627\u0644\u0625\u0634\u0639\u0627\u0631\u0627\u062a", "Notifications", "Notifications")}
        title={t("\u0627\u0644\u0625\u0634\u0639\u0627\u0631\u0627\u062a", "Notifications", "Notifications")}
        className="relative grid place-items-center w-9 h-9 rounded-full bg-secondary text-secondary-foreground hover:bg-accent hover:text-accent-foreground border border-border/70 shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <Icon className={`h-4 w-4 ${working ? "animate-spin" : ""}`} />
        {status === "default" && !open && (
          <span className="pointer-events-none absolute inset-0 rounded-full ring-2 ring-primary/60 animate-pulse" aria-hidden />
        )}
        {status === "granted" && unread === 0 && !open && (
          <span className="absolute -top-0.5 -end-0.5 grid place-items-center w-3.5 h-3.5 rounded-full bg-emerald-500 ring-2 ring-background" aria-hidden>
            <Check className="h-2 w-2 text-white" strokeWidth={4} />
          </span>
        )}
        {badge && (
          <span
            aria-label={`${unread} ${t("\u062c\u062f\u064a\u062f", "new", "NOUVEAU")}`}
            className="absolute -top-1 -end-1 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-extrabold grid place-items-center ring-2 ring-background tabular-nums"
          >
            {badge}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Backdrop on mobile only */}
          <div className="sm:hidden fixed inset-0 bg-black/30 z-40" aria-hidden onClick={() => setOpen(false)} />

          <div
            role="dialog"
            aria-label={t("\u0627\u0644\u0625\u0634\u0639\u0627\u0631\u0627\u062a", "Notifications", "Notifications")}
            className={[
              "z-50",
              // Mobile: bottom sheet style spanning width
              "fixed inset-x-2 top-16 sm:inset-x-auto",
              // Desktop: anchored under bell with same offset as language/theme panels; respect RTL (start/end)
              "sm:absolute sm:end-0 sm:start-auto sm:top-[calc(100%+0.75rem)]",
              "sm:w-[360px] max-h-[80vh] overflow-hidden",
              "rounded-2xl border border-border bg-card shadow-xl",
              "flex flex-col",
              // Match language/theme popovers animation on desktop
              "animate-fade-in sm:animate-scale-in",
            ].join(" ")}

          >
            {/* Header */}
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2 min-w-0">
                <Bell className="h-4 w-4 text-primary shrink-0" />
                <h3 className="font-bold text-sm truncate">{t("\u0627\u0644\u0625\u0634\u0639\u0627\u0631\u0627\u062a", "Notifications", "Notifications")}</h3>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="grid place-items-center w-7 h-7 rounded-md hover:bg-accent"
                aria-label={t("\u0625\u063a\u0644\u0627\u0642", "Close", "Fermer")}
              >
                <XIcon className="h-4 w-4" />
              </button>
            </div>

            {/* SECTION 1 — browser push status. Inside Telegram Mini App
                the WebView has no Notification API, so we swap the enable
                button for a Telegram-native hint instead of an "unavailable"
                dead-end button. */}
            {status === "unsupported" && typeof document !== "undefined" && document.documentElement.classList.contains("is-telegram") ? (
              <div className="px-4 py-3 border-b border-border bg-background/40">
                <div className="flex items-start gap-3">
                  <div className="grid place-items-center w-9 h-9 rounded-xl shrink-0 bg-primary/10 text-primary">
                    <BellRing className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold">
                      {t("التنبيهات عبر بوت تلغرام", "Notifications via Telegram bot")}
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                      {t("أنت داخل تطبيق تلغرام المصغّر — الإشعارات تصلك مباشرة من البوت عند نزول حلقة أو خبر جديد. لا حاجة لتفعيل شيء.",
                         "You're inside the Telegram Mini App — the bot pushes new episodes and news to you automatically.")}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="px-4 py-3 border-b border-border bg-background/40">
                <div className="flex items-start gap-3">
                  <div className={`grid place-items-center w-9 h-9 rounded-xl shrink-0 ${
                    status === "granted" ? "bg-emerald-500/15 text-emerald-600"
                    : status === "denied" ? "bg-destructive/15 text-destructive"
                    : "bg-primary/10 text-primary"
                  }`}>
                    {status === "granted" ? <BellRing className="h-4 w-4" />
                     : status === "denied" || status === "unsupported" ? <BellOff className="h-4 w-4" />
                     : <Bell className="h-4 w-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold">
                      {status === "granted"
                        ? t("إشعارات المتصفح مفعّلة", "Browser push enabled")
                        : status === "unsupported"
                          ? t("إشعارات المتصفح غير متاحة هنا", "Browser push unavailable here")
                        : status === "denied"
                          ? t("الإشعارات محظورة من المتصفح", "Blocked by browser")
                          : t("إشعارات المتصفح غير مفعّلة", "Browser push not enabled")}
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                      {status === "unsupported"
                        ? t("متصفحك الحالي لا يدعم إشعارات الويب. جرّب متصفحاً حديثاً أو ثبّت الموقع كتطبيق.",
                            "Your current browser doesn't support web push. Try a modern browser or install the site as an app.")
                        : status === "denied"
                        ? t("افتح إعدادات الموقع في المتصفح للسماح بالإشعارات.",
                            "Open the site settings in your browser to allow notifications.")
                        : t("احصل على تنبيه فوري عند نزول حلقات أو أخبار جديدة.",
                            "Get an instant alert for new episodes & news.")}
                    </p>
                  </div>
                  {status !== "unsupported" && (
                    <button
                      type="button"
                      onClick={toggleFcm}
                      disabled={working || status === "denied" || status === "granted"}
                      className="btn-primary !px-3 !py-1.5 text-[11px] disabled:opacity-50 shrink-0 inline-flex items-center gap-1"
                    >
                      {working ? <Loader2 className="h-3 w-3 animate-spin" />
                       : status === "granted" ? <Check className="h-3 w-3" />
                       : null}
                      {status === "granted"
                        ? t("مفعّلة", "On")
                        : t("تفعيل", "Enable")}
                    </button>
                  )}
                </div>
              </div>
            )}


            {/* SECTION 2 — interactions inbox */}
            <div className="flex-1 overflow-y-auto">
              <div className="px-4 py-2 text-[11px] font-bold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                <Inbox className="h-3.5 w-3.5" />
                <span>{t("الردود والتفاعلات", "Replies & reactions")}</span>
                {user && items.length > 0 && (
                  <button
                    type="button"
                    onClick={handleClearAll}
                    disabled={clearing}
                    className={`ms-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-semibold transition-colors disabled:opacity-50 ${
                      confirmClear
                        ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        : "bg-muted text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    }`}
                    aria-label={t("تنظيف الصندوق", "Clear inbox")}
                  >
                    {clearing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                    <span>
                      {confirmClear
                        ? t("تأكيد", "Confirm")
                        : t("تنظيف", "Clear")}
                    </span>
                  </button>
                )}
              </div>
              {!user ? (
                <div className="px-4 py-6 text-center text-xs text-muted-foreground">
                  {t("سجّل دخولك لرؤية إشعارات تعليقاتك.",
                     "Sign in to see notifications for your comments.")}
                </div>
              ) : items.length === 0 ? (
                <div className="px-4 py-8 text-center text-xs text-muted-foreground">
                  {t("لا توجد إشعارات بعد.", "No notifications yet.")}
                </div>
              ) : (
                <SmartNotifList items={items} isAr={isAr} onNavigate={() => setOpen(false)} />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function NotifRow({
  n, isAr, onNavigate,
}: { n: AppNotification; isAr: boolean; onNavigate: () => void }) {
  const { language } = useTheme();
  const t = (ar: string, en: string, fr?: string) => (language === "ar" ? ar : language === "fr" ? (fr ?? en) : en);
  // Notifications open a dedicated lightweight comments page instead of
  // loading the full movie/series page. This avoids player/ads/related-content
  // work while resolving reply/reaction deep links.
  let href: string | null = null;
  if (n.threadId) {
    const tid = encodeURIComponent(n.threadId);
    const anchor = n.commentId ? `#comment-${n.commentId}` : "#comments";
    const search = new URLSearchParams();
    if (n.commentId) search.set("focus", n.commentId);
    if (n.threadTitle) search.set("t", n.threadTitle);
    const qs = search.toString();
    href = `/c/${tid}${qs ? `?${qs}` : ""}${anchor}`;
  }
  const label = n.type === "reply"
    ? t(`ردّ ${n.actorName} على تعليقك`, `${n.actorName} replied to your comment`, `${n.actorName} a répondu à votre commentaire`)
    : n.type === "thread"
    ? t(
        `${n.actorName} علّق على ${n.threadTitle ?? "عمل حفظته"}`,
        `${n.actorName} commented on ${n.threadTitle ?? "an item you saved"}`,
        `${n.actorName} a commenté ${n.threadTitle ?? "un titre enregistré"}`,
      )
    : t(`${n.actorName} تفاعل مع تعليقك`, `${n.actorName} reacted to your comment`, `${n.actorName} a réagi à votre commentaire`);

  const Body = (
    <div className={`px-4 py-3 flex gap-3 items-start hover:bg-accent/40 transition ${!n.read ? "bg-primary/5" : ""}`}>
      <div className="relative shrink-0">
        {n.actorAvatar ? (
          <img src={n.actorAvatar} alt="" className="w-8 h-8 rounded-full object-cover border border-border" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-muted grid place-items-center text-[11px] font-bold">
            {n.actorName.slice(0, 1).toUpperCase()}
          </div>
        )}
        <span className="absolute -bottom-0.5 -end-0.5 grid place-items-center w-4 h-4 rounded-full bg-background border border-border">
          {reactionIcon(n.reactionKind)}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold leading-snug">{label}</div>
        {n.snippet && (
          <div className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">«{n.snippet}»</div>
        )}
        {n.threadTitle && (
          <div className="text-[10px] text-muted-foreground mt-0.5 truncate">{n.threadTitle}</div>
        )}
        <div className="text-[10px] text-muted-foreground mt-0.5">{timeAgo(n.createdAt, isAr)}</div>
      </div>
      {!n.read && <span className="w-2 h-2 rounded-full bg-primary mt-1 shrink-0" aria-hidden />}
    </div>
  );

  return (
    <li>
      {href ? (
        // Use a plain anchor (not <Link>) so the browser keeps the
        // `#comment-<id>` hash. TanStack's <Link to="..."> treats `to` as a
        // route path and would drop / mismatch the hash on cross-route nav.
        // A normal navigation also guarantees CommentsSection's scroll-and-
        // highlight effect runs after the destination page has mounted.
        <a
          href={href}
          onClick={() => {
            if (n.commentId) storeCommentDeepLinkIntent(n.commentId, href);
            onNavigate();
          }}
          className="block"
        >
          {Body}
        </a>
      ) : (
        Body
      )}
    </li>
  );
}


/* ───────────── Smart grouped notification list ─────────────
 * - Newest first (items already arrive sorted desc by createdAt).
 * - Default: collapsed to MAX_VISIBLE rows + "Show all" toggle.
 * - Reactions on the SAME comment are folded into a single row
 *   ("3 people reacted to your comment"). Replies stay individual.
 * - Each row links to /c/<threadId>?focus=<id>#comment-<id>.
 */
const MAX_VISIBLE = 3;

type Group =
  | { kind: "single"; key: string; n: AppNotification }
  | { kind: "stack"; key: string; items: AppNotification[] };

function groupNotifications(items: AppNotification[]): Group[] {
  const out: Group[] = [];
  const stacks = new Map<string, number>(); // key -> out index
  for (const n of items) {
    // Stack reactions by commentId, and thread-fan-out notifications by threadId.
    const stackKey =
      n.type === "reaction" && n.commentId ? `r:${n.commentId}`
      : n.type === "thread" && n.threadId ? `t:${n.threadId}`
      : null;
    if (stackKey) {
      const idx = stacks.get(stackKey);
      if (idx != null && out[idx].kind === "stack") {
        (out[idx] as { kind: "stack"; items: AppNotification[] }).items.push(n);
        continue;
      }
      const g: Group = { kind: "stack", key: stackKey, items: [n] };
      stacks.set(stackKey, out.length);
      out.push(g);
    } else {
      out.push({ kind: "single", key: `s:${n.id}`, n });
    }
  }
  return out;
}

function SmartNotifList({
  items, isAr, onNavigate,
}: { items: AppNotification[]; isAr: boolean; onNavigate: () => void }) {
  const { language } = useTheme();
  const [showAll, setShowAll] = useState(false);
  const [openStacks, setOpenStacks] = useState<Record<string, boolean>>({});
  const t = (ar: string, en: string, fr?: string) => (language === "ar" ? ar : language === "fr" ? (fr ?? en) : en);

  const groups = groupNotifications(items);
  const visible = showAll ? groups : groups.slice(0, MAX_VISIBLE);
  const hiddenCount = groups.length - visible.length;

  return (
    <>
      <ul className="divide-y divide-border">
        {visible.map((g) => {
          if (g.kind === "single") {
            return <NotifRow key={g.key} n={g.n} isAr={isAr} onNavigate={onNavigate} />;
          }
          const isOpen = !!openStacks[g.key];
          const head = g.items[0]; // newest
          const more = g.items.length - 1;
          const anyUnread = g.items.some((x) => !x.read);
          return (
            <li key={g.key}>
              <button
                type="button"
                onClick={() => setOpenStacks((m) => ({ ...m, [g.key]: !isOpen }))}
                className={`w-full text-start px-4 py-3 flex gap-3 items-start hover:bg-accent/40 transition ${anyUnread ? "bg-primary/5" : ""}`}
              >
                <div className="relative shrink-0">
                  {head.actorAvatar ? (
                    <img src={head.actorAvatar} alt="" className="w-8 h-8 rounded-full object-cover border border-border" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-muted grid place-items-center text-[11px] font-bold">
                      {head.actorName.slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <span className="absolute -bottom-0.5 -end-0.5 grid place-items-center w-4 h-4 rounded-full bg-background border border-border">
                    {reactionIcon(head.reactionKind)}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold leading-snug">
                    {head.type === "thread"
                      ? (more > 0
                          ? t(
                              `${head.actorName} و${more} آخرون علّقوا على ${head.threadTitle ?? "عمل حفظته"}`,
                              `${head.actorName} and ${more} other${more === 1 ? "" : "s"} commented on ${head.threadTitle ?? "an item you saved"}`,
                              `${head.actorName} et ${more} autre${more === 1 ? "" : "s"} ont commenté ${head.threadTitle ?? "un titre enregistré"}`,
                            )
                          : t(
                              `${head.actorName} علّق على ${head.threadTitle ?? "عمل حفظته"}`,
                              `${head.actorName} commented on ${head.threadTitle ?? "an item you saved"}`,
                              `${head.actorName} a commenté ${head.threadTitle ?? "un titre enregistré"}`,
                            ))
                      : (more > 0
                          ? t(`${head.actorName} و${more} آخرون تفاعلوا مع تعليقك`,
                               `${head.actorName} and ${more} other${more === 1 ? "" : "s"} reacted to your comment`,
                               `${head.actorName} et ${more} autre${more === 1 ? "" : "s"} ont réagi à votre commentaire`)
                          : t(`${head.actorName} تفاعل مع تعليقك`,
                               `${head.actorName} reacted to your comment`,
                               `${head.actorName} a réagi à votre commentaire`))}
                  </div>
                  {head.snippet && (
                    <div className="text-[11px] text-muted-foreground line-clamp-1 mt-0.5">«{head.snippet}»</div>
                  )}
                  {head.threadTitle && (
                    <div className="text-[10px] text-muted-foreground mt-0.5 truncate">{head.threadTitle}</div>
                  )}
                  <div className="text-[10px] text-muted-foreground mt-0.5">{timeAgo(head.createdAt, isAr)}</div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  {anyUnread && <span className="w-2 h-2 rounded-full bg-primary" aria-hidden />}
                  {isOpen ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
                          : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
                </div>
              </button>
              {isOpen && (
                <ul className="bg-background/50 border-t border-border">
                  {g.items.map((n) => (
                    <NotifRow key={n.id} n={n} isAr={isAr} onNavigate={onNavigate} />
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
      {(hiddenCount > 0 || showAll) && groups.length > MAX_VISIBLE && (
        <div className="px-4 py-2 border-t border-border bg-background/30">
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="w-full inline-flex items-center justify-center gap-1.5 py-1.5 rounded-md hover:bg-accent text-[11px] font-semibold text-muted-foreground"
          >
            {showAll ? (
              <>
                <ChevronUp className="h-3.5 w-3.5" />
                {t("طي القائمة", "Show less")}
              </>
            ) : (
              <>
                <ChevronDown className="h-3.5 w-3.5" />
                {t(`عرض ${hiddenCount} إشعارات إضافية`, `Show ${hiddenCount} more`)}
              </>
            )}
          </button>
        </div>
      )}
    </>
  );
}
